/** Extra padding added around a city's geocoded bounding box. */
export const CITY_BBOX_PAD_MILES = 8;

/** Approximate statute miles per degree of latitude. */
const MILES_PER_DEG_LAT = 69.0;

const CITY_QUERY_MIN_CHARS = 2;

/**
 * @typedef {Object} GeoBBox
 * @property {number} west
 * @property {number} south
 * @property {number} east
 * @property {number} north
 */

/**
 * @typedef {Object} ExplorePlace
 * @property {string} display
 * @property {string} [city]
 * @property {string} [state]
 * @property {string} [country]
 * @property {number|null} [lat]
 * @property {number|null} [lon]
 * @property {GeoBBox} bbox
 */

/**
 * Parse a finite number, otherwise null.
 * @param {unknown} raw
 * @returns {number|null}
 */
export function toFloatOrNull(raw) {
  if (raw === null || raw === undefined || raw === "") {
    return null;
  }
  const parsed = typeof raw === "number" ? raw : parseFloat(String(raw));
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Expand a bounding box by a uniform distance in miles on each side.
 * @param {GeoBBox} bbox
 * @param {number} [miles=CITY_BBOX_PAD_MILES]
 * @returns {GeoBBox}
 */
export function expandBBoxByMiles(bbox, miles = CITY_BBOX_PAD_MILES) {
  const midLat = (bbox.south + bbox.north) / 2;
  const latPad = miles / MILES_PER_DEG_LAT;
  const milesPerDegLon = Math.max(
    0.01,
    MILES_PER_DEG_LAT * Math.cos((midLat * Math.PI) / 180)
  );
  const lonPad = miles / milesPerDegLon;
  return {
    west: bbox.west - lonPad,
    south: bbox.south - latPad,
    east: bbox.east + lonPad,
    north: bbox.north + latPad,
  };
}

/**
 * Parse Nominatim `boundingbox` (`[south, north, west, east]`) into a GeoBBox.
 * @param {unknown} raw
 * @returns {GeoBBox|null}
 */
export function parseNominatimBoundingBox(raw) {
  if (!Array.isArray(raw) || raw.length < 4) {
    return null;
  }
  const south = toFloatOrNull(raw[0]);
  const north = toFloatOrNull(raw[1]);
  const west = toFloatOrNull(raw[2]);
  const east = toFloatOrNull(raw[3]);
  if (south === null || north === null || west === null || east === null) {
    return null;
  }
  if (south > north || west > east) {
    return null;
  }
  return { west, south, east, north };
}

/**
 * Build a square-ish box around a point, used when Nominatim omits a bbox.
 * @param {number} lat
 * @param {number} lon
 * @param {number} [halfMiles=10]
 * @returns {GeoBBox}
 */
export function bboxFromPoint(lat, lon, halfMiles = 10) {
  const latPad = halfMiles / MILES_PER_DEG_LAT;
  const milesPerDegLon = Math.max(
    0.01,
    MILES_PER_DEG_LAT * Math.cos((lat * Math.PI) / 180)
  );
  const lonPad = halfMiles / milesPerDegLon;
  return {
    west: lon - lonPad,
    south: lat - latPad,
    east: lon + lonPad,
    north: lat + latPad,
  };
}

/**
 * Serialize a bbox as `west,south,east,north` for the explore URL.
 * @param {GeoBBox} bbox
 * @returns {string}
 */
export function serializeBBox(bbox) {
  return [bbox.west, bbox.south, bbox.east, bbox.north].join(",");
}

/**
 * Parse `west,south,east,north` from the explore URL.
 * @param {string|null|undefined} raw
 * @returns {GeoBBox|null}
 */
export function parseBBoxParam(raw) {
  if (!raw || typeof raw !== "string") {
    return null;
  }
  const parts = raw.split(",");
  if (parts.length !== 4) {
    return null;
  }
  const west = toFloatOrNull(parts[0]);
  const south = toFloatOrNull(parts[1]);
  const east = toFloatOrNull(parts[2]);
  const north = toFloatOrNull(parts[3]);
  if (west === null || south === null || east === null || north === null) {
    return null;
  }
  if (south > north || west > east) {
    return null;
  }
  return { west, south, east, north };
}

/**
 * Format a Nominatim city result as "City, State, Country".
 * @param {Object} place
 * @returns {ExplorePlace|null}
 */
export function formatNominatimCity(place) {
  if (!place) {
    return null;
  }
  const address = place.address || {};
  const city =
    address.city ||
    address.town ||
    address.village ||
    address.municipality ||
    String(place.display_name || "").split(",")[0] ||
    "";
  const state = address.state || address.region || "";
  const country = address.country || "";
  const parts = [city];
  if (state) parts.push(state);
  if (country && country !== state) parts.push(country);
  const display = parts.filter(Boolean).join(", ");
  if (!display) {
    return null;
  }
  const lat = toFloatOrNull(place.lat);
  const lon = toFloatOrNull(place.lon);
  const rawBBox = parseNominatimBoundingBox(place.boundingbox);
  const bbox =
    rawBBox ||
    (lat !== null && lon !== null ? bboxFromPoint(lat, lon) : null);
  if (!bbox) {
    return null;
  }
  return {
    display,
    city,
    state,
    country,
    lat,
    lon,
    bbox: expandBBoxByMiles(bbox),
  };
}

/**
 * True when the typed query is a close match for this city (prefix of the
 * city name, display string, or a later word in the city name).
 * @param {string} query
 * @param {ExplorePlace} city
 * @returns {boolean}
 */
export function isCloseCityMatch(query, city) {
  const q = (query || "").trim().toLowerCase();
  if (q.length < CITY_QUERY_MIN_CHARS || !city) {
    return false;
  }
  const cityName = (city.city || "").toLowerCase();
  const display = (city.display || "").toLowerCase();
  if (display.startsWith(q) || cityName.startsWith(q)) {
    return true;
  }
  return cityName.split(/[\s,/-]+/).some((word) => word.startsWith(q));
}

/**
 * Pick the best in-bar city suggestion for the current query.
 * @param {string} query
 * @param {ExplorePlace[]} results
 * @returns {ExplorePlace|null}
 */
export function pickCloseCityMatch(query, results) {
  if (!Array.isArray(results) || results.length === 0) {
    return null;
  }
  return results.find((city) => isCloseCityMatch(query, city)) || null;
}

/**
 * Split typed text vs ghost completion for inline autocomplete.
 * @param {string} query
 * @param {ExplorePlace} city
 * @returns {{ prefix: string, suffix: string }}
 */
export function cityGhostParts(query, city) {
  const prefix = query || "";
  const display = city?.display || "";
  const trimmed = prefix.trim();
  if (
    trimmed &&
    display.toLowerCase().startsWith(trimmed.toLowerCase())
  ) {
    return { prefix, suffix: display.slice(trimmed.length) };
  }
  return { prefix, suffix: display ? `\u00a0${display}` : "" };
}

/**
 * Read a committed explore place from URL search params.
 * @param {URLSearchParams} searchParams
 * @returns {ExplorePlace|null}
 */
export function parseExplorePlace(searchParams) {
  const display = (searchParams.get("place") || "").trim();
  const bbox = parseBBoxParam(searchParams.get("bbox"));
  if (!display || !bbox) {
    return null;
  }
  return { display, bbox };
}

/**
 * Write query + optional place onto explore search params.
 * Clears post-location focus (`lat`/`lon`/`label`) so city search can take over.
 * @param {string} currentSearch
 * @param {{ query?: string, place?: ExplorePlace|null }} next
 * @returns {string}
 */
export function buildExploreSearchParams(currentSearch, next) {
  const params = new URLSearchParams(currentSearch);
  params.delete("lat");
  params.delete("lon");
  params.delete("lng");
  params.delete("label");

  const query = (next.query || "").trim();
  if (query) {
    params.set("q", next.query);
  } else {
    params.delete("q");
  }

  if (next.place?.display && next.place?.bbox) {
    params.set("place", next.place.display);
    params.set("bbox", serializeBBox(next.place.bbox));
  } else {
    params.delete("place");
    params.delete("bbox");
  }

  return params.toString();
}
